import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { House, UserCircle, SignOut, SignIn } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global navigation bar shown on every page. Self-contained:
 * - Landing, login, register, forgot-password and the public Dashboard hide
 *   themselves via the `HIDE_ON` list (Dashboard mounts its own DashboardNav).
 * - Links the Show Master logo + Home button to /dashboard when logged in,
 *   otherwise to / so unauthenticated riders land on the marketing page.
 */
const HIDE_ON = new Set(['/', '/login', '/register', '/forgot-password', '/dashboard']);

// Hide on kiosk routes too (they have their own chrome).
const isHiddenPath = (p) => {
  if (HIDE_ON.has(p)) return true;
  if (p.startsWith('/kiosk')) return true;
  return false;
};

export const GlobalNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  if (isHiddenPath(location.pathname)) return null;

  const homePath = user ? '/dashboard' : '/';

  return (
    <nav
      className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40"
      data-testid="global-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          <button
            onClick={() => navigate(homePath)}
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
            data-testid="global-nav-logo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_2decf1d9-6688-4b47-98c1-1893beae897a/artifacts/lr28yf8y_43425DE4-4AE4-4784-89F6-4AA746534747.png"
              alt="Show Master"
              className="h-10 w-10 sm:h-[52px] sm:w-[52px]"
            />
            <span
              className="text-sm sm:text-lg font-black text-white"
              style={{ fontFamily: 'Outfit' }}
            >
              SHOW MASTER
            </span>
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => navigate(homePath)}
              className="p-2 text-[#9E9E9E] hover:text-[#0096FF] transition-colors"
              title="Home"
              data-testid="global-nav-home"
            >
              <House size={22} weight="duotone" />
            </button>
            {user ? (
              <>
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2 text-[#9E9E9E] hover:text-[#0096FF] transition-colors"
                  title="My Profile"
                  data-testid="global-nav-profile"
                >
                  <UserCircle size={22} weight="duotone" />
                </button>
                <button
                  onClick={logout}
                  className="p-2 text-[#9E9E9E] hover:text-white transition-colors"
                  title="Log out"
                  data-testid="global-nav-logout"
                >
                  <SignOut size={20} weight="duotone" />
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF] hover:bg-[#0096FF]/90 text-white text-xs sm:text-sm font-bold rounded-md transition-colors"
                data-testid="global-nav-login"
              >
                <SignIn size={14} weight="bold" /> Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
